import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Fish, MapPin, Trophy, Calendar, TrendingUp, Heart, ArrowRight, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import WeatherWidget from '../components/WeatherWidget';
import CatchOfTheMonth from '../components/CatchOfTheMonth';
import CatchImage from '../components/CatchImage';
import SmartRecommendations from '../components/SmartRecommendations';
import NearestLakes from '../components/NearestLakes';
import TrialBanner from '../components/TrialBanner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const [recentCatches, setRecentCatches] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [stats, setStats] = useState({ total_members: 0, total_lakes: 0, total_catches: 0 });
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [catchesRes, eventsRes, statsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/catches?limit=5`, { headers }),
        axios.get(`${API}/events?upcoming=true`, { headers }),
        axios.get(`${API}/stats`),
        axios.get(`${API}/leaderboard?limit=5`, { headers })
      ]);
      setRecentCatches(catchesRes.data);
      setUpcomingEvents(eventsRes.data.slice(0, 3));
      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      elite: 'bg-gradient-to-r from-[#c8b273] to-[#96834d] text-black',
      pro: 'bg-[#ff5e00] text-white',
      basic: 'bg-[#2d352d] text-[#6b7d6b]'
    };
    return colors[tier] || colors.basic;
  };

  return (
    <div className="min-h-screen p-6 md:p-8" data-testid="dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="data-label text-[#c8b273] mb-2">Welcome Back</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white">
            {user?.username}
          </h1>
          <span className={`inline-block mt-2 px-3 py-1 rounded text-xs font-bold uppercase tracking-widest ${getTierBadge(user?.subscription_tier)}`}>
            {user?.subscription_tier} Member
          </span>
        </motion.div>

        {/* 3-day Pro Trial Banner (only shows when trial_active is true) */}
        <TrialBanner user={user} />

        {/* Stats Cards — clickable shortcuts to relevant pages */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Fish,     label: 'Your Catches',  value: user?.catches_count || 0, to: '/community', testid: 'stat-card-your-catches' },
            { icon: MapPin,   label: 'Total Lakes',   value: stats.total_lakes,        to: '/lakes',     testid: 'stat-card-lakes' },
            { icon: Trophy,   label: 'Total Catches', value: stats.total_catches,      to: '/community', testid: 'stat-card-total-catches' },
            { icon: Calendar, label: 'Members',       value: stats.total_members,      to: '/community', testid: 'stat-card-members' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={stat.to}
                data-testid={stat.testid}
                className="glass-card p-4 block transition-transform duration-200 hover:scale-[1.02] hover:border-[#c8b273]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c8b273]/60 cursor-pointer"
                aria-label={`${stat.label}: ${stat.value}. Open ${stat.to.replace('/', '')} page.`}
              >
                <stat.icon className="w-6 h-6 text-[#c8b273] mb-2" />
                <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">{stat.value}</p>
                <p className="data-label text-[#8a948a] text-xs">{stat.label}</p>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Catch of the Month */}
        <CatchOfTheMonth />

        {/* Smart Recommendations (Pro/Elite full view, Basic teaser) */}
        <SmartRecommendations />

        {/* Pro-only: nearest lakes by saved weather location */}
        <NearestLakes />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Weather Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-[#c8b273]" />
                  Live Weather
                </h2>
              </div>
              <WeatherWidget expanded={true} />
            </motion.div>

            {/* Recent Catches Feed */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
                  Recent Catches
                </h2>
                <Link to="/community">
                  <Button variant="ghost" className="btn-ghost text-sm" data-testid="view-all-catches">
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>

              <div className="space-y-4">
                {recentCatches.length === 0 ? (
                  <div className="glass-card p-8 text-center">
                    <Fish className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
                    <p className="text-[#8a948a]">No catches yet. Be the first to log one!</p>
                    <Link to="/lakes">
                      <Button className="btn-gold mt-4">Find a Lake</Button>
                    </Link>
                  </div>
                ) : (
                  recentCatches.map((catch_, index) => (
                    <motion.div
                      key={catch_.id}
                      className="glass-card p-4 flex gap-4"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <CatchImage row={catch_} aspectRatio="square" rounded className="w-20 h-20 flex-shrink-0" testId={`dashboard-catch-image-${catch_.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-white">{catch_.username}</span>
                          <span className="text-[#4a504a]">•</span>
                          <span className="text-[#8a948a] text-sm">{catch_.lake_name}</span>
                        </div>
                        <p className="text-[#c8b273] font-['Barlow_Condensed'] text-lg font-bold uppercase">
                          {catch_.fish_type} - {catch_.weight}lb
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="flex items-center gap-1 text-[#8a948a] text-sm">
                            <Heart className="w-4 h-4" />
                            {catch_.likes_count}
                          </span>
                          <span className="text-[#4a504a] text-sm">
                            {new Date(catch_.caught_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Leaderboard */}
            <div>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                Leaderboard
              </h2>
              <div className="glass-card divide-y divide-white/5">
                {leaderboard.length === 0 ? (
                  <div className="p-4 text-center text-[#8a948a]">
                    No catches recorded yet
                  </div>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div key={entry.id} className="p-3 flex items-center gap-3">
                      <span className={`w-6 h-6 rounded flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-[#c8b273] text-black' :
                        index === 1 ? 'bg-[#8a948a] text-black' :
                        index === 2 ? 'bg-[#96834d] text-black' :
                        'bg-[#141714] text-[#8a948a]'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{entry.username}</p>
                        <p className="text-[#8a948a] text-xs">{entry.fish_type}</p>
                      </div>
                      <span className="font-['Barlow_Condensed'] text-[#c8b273] font-bold">
                        {entry.weight}lb
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Upcoming Events */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
                  Events
                </h2>
                <Link to="/events">
                  <Button variant="ghost" className="text-[#c8b273] text-sm p-0">
                    View All
                  </Button>
                </Link>
              </div>
              <div className="space-y-3">
                {upcomingEvents.length === 0 ? (
                  <div className="glass-card p-4 text-center text-[#8a948a]">
                    No upcoming events
                  </div>
                ) : (
                  upcomingEvents.map((event) => (
                    <Link key={event.id} to="/events">
                      <div className="glass-card p-4 hover:border-[#c8b273]/30 transition-colors">
                        <p className="font-['Barlow_Condensed'] text-white font-bold uppercase tracking-wide">
                          {event.title}
                        </p>
                        <p className="text-[#8a948a] text-sm mt-1">
                          {new Date(event.event_date).toLocaleDateString()}
                        </p>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="glass-card p-4">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-4">
                Quick Actions
              </h3>
              <div className="space-y-2">
                <Link to="/lakes" className="block">
                  <Button className="btn-ghost w-full justify-start">
                    <MapPin className="w-4 h-4 mr-2" />
                    Find a Lake
                  </Button>
                </Link>
                <Link to="/shop" className="block">
                  <Button className="btn-ghost w-full justify-start">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Browse Shop
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
